import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  People as PeopleIcon,
  Lock as LockIcon,
  List as ListIcon,
  Home as HomeIcon,
  Assessment,
  LocationOn as LocationOnIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@mui/material/styles';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleUserMenuClose();
  };

  if (!user) return null;

  const isSuperAdmin = user.role === 'super_admin';

  return (
    <AppBar
      position="static"
      sx={{
        flexShrink: 0,
        borderRadius: 0,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1d388d 0%, #0d1f4d 50%, #1d388d 100%)'
          : 'linear-gradient(135deg, #1d388d 0%, #2a4ba0 50%, #1d388d 100%)',
        backgroundSize: '200% 200%',
        animation: `${keyframes`
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        `} 15s ease infinite`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(29, 56, 141, 0.4)'
          : '0 4px 20px rgba(29, 56, 141, 0.3)',
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
        <Box
          onClick={handleLogoClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mr: { xs: 1, sm: 3 },
            minWidth: 0,
            flexShrink: 1,
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        >
          <Box
            component="img"
            src={isCompact
              ? '/assets/om-client-manager-vertical-white.png'
              : '/assets/om-client-manager-horizontal-white.png'}
            alt={t('common.appName')}
            sx={{
              width: '100%',
              maxWidth: isCompact ? 130 : 380,
              height: 'auto',
              display: 'block',
              bgcolor: 'transparent',
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right side controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: { xs: 0, sm: 1 },
            '& .MuiIconButton-root': { p: { xs: 0.75, sm: 1 } },
          }}
        >
          {/* Home Button */}
          <Tooltip title={t('navigation.dashboard')}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/')}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>

          {/* CPE List Button */}
          <Tooltip title={t('navigation.cpeList')}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/cpes')}
            >
              <ListIcon />
            </IconButton>
          </Tooltip>

          {/* Zones Button */}
          <Tooltip title={t('navigation.zoneList')}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/zones')}
            >
              <LocationOnIcon />
            </IconButton>
          </Tooltip>

          {/* Metrics Button */}
          <Tooltip title={t('navigation.metrics', 'Metrics')}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/metrics')}
            >
              <Assessment />
            </IconButton>
          </Tooltip>

          <ThemeToggle />
          <LanguageSwitcher />

          {/* User Menu */}
          <Tooltip title={t('common.account', 'Account')}>
            <IconButton
              color="inherit"
              onClick={handleUserMenuOpen}
              sx={{ ml: { xs: 0, sm: 1 } }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {/* User Info Section */}
            <Box sx={{ px: 2, py: 1.5, minWidth: 250 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('common.signedInAs', 'Signed in as')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                {user.email}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  display: 'inline-block',
                  mt: 0.5,
                }}
              >
                {user.role === 'super_admin'
                  ? t('users.roles.superAdmin')
                  : user.role === 'admin'
                  ? t('users.roles.admin')
                  : t('users.roles.user')}
              </Typography>
            </Box>

            <Divider />

            {/* Settings */}
            <MenuItem onClick={() => handleNavigate('/change-password')}>
              <ListItemIcon>
                <LockIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('auth.changePassword')}</ListItemText>
            </MenuItem>

            {/* User Management (Super Admin only) */}
            {isSuperAdmin && (
              <MenuItem onClick={() => handleNavigate('/users')}>
                <ListItemIcon>
                  <PeopleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('navigation.userManagement')}</ListItemText>
              </MenuItem>
            )}

            {/* Settings (Admin and Super Admin) */}
            {isAdmin && (
              <MenuItem onClick={() => handleNavigate('/settings')}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('navigation.settings', 'Settings')}</ListItemText>
              </MenuItem>
            )}

            <Divider />

            {/* Logout */}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

